import React, { useState, useEffect } from 'react';
import AppShell from '../components/AppShell';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/Skeleton';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  GitPullRequest, 
  MessageSquare, 
  StickyNote, 
  AlertCircle,
  ExternalLink,
  RefreshCw,
  TrendingUp,
  Clock,
  Eye,
  FolderKanban,
  CheckCircle2,
  AlertTriangle,
  Info,
  Search,
  Calendar as CalendarIcon,
  History
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';
import { ThinkingIndicator } from '../components/ThinkingIndicator';
import { format, subDays, subWeeks, subMonths, startOfDay, endOfDay, differenceInDays } from 'date-fns';

export default function CustomScanPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 14), // Default: 2 weeks ago
    to: new Date()
  });
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [historicalScans, setHistoricalScans] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedScanId, setSelectedScanId] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      loadHistoricalScans();
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProjectId) {
        setSelectedProjectId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadHistoricalScans = async () => {
    if (!selectedProjectId) return;
    try {
      setLoadingHistory(true);
      const response = await api.get(`/projects/${selectedProjectId}/scan-results`, {
        params: { scan_type: 'custom_scan', limit: 20 }
      });
      setHistoricalScans(response.data.scan_results || []);
    } catch (error) {
      console.error('Failed to load historical scans:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const validateDateRange = () => {
    if (!dateRange.from || !dateRange.to) {
      return { valid: false, error: 'Please select both start and end dates' };
    }
    
    const daysDiff = differenceInDays(dateRange.to, dateRange.from);
    if (daysDiff < 0) {
      return { valid: false, error: 'End date must be after start date' };
    }
    
    if (daysDiff > 90) {
      return { valid: false, error: 'Date range cannot exceed 3 months (90 days)' };
    }
    
    return { valid: true };
  };

  const loadCustomScan = async (showRefreshing = false) => {
    if (!selectedProjectId) return;

    const validation = validateDateRange();
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const since = startOfDay(dateRange.from).toISOString();
      const until = endOfDay(dateRange.to).toISOString();
      
      const response = await api.get(`/projects/${selectedProjectId}/morning-scan`, {
        params: { since, until, save_result: true }
      });

      setData(response.data);
      setSelectedScanId(null); // Clear selected scan since we have new data
      await loadHistoricalScans(); // Refresh history
    } catch (error) {
      console.error('Failed to load custom scan:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to load custom scan';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadHistoricalScan = async (scanId) => {
    if (!selectedProjectId || !scanId) return;
    try {
      setLoading(true);
      setSelectedScanId(scanId);
      const response = await api.get(`/projects/${selectedProjectId}/scan-results/${scanId}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load historical scan:', error);
      toast.error('Failed to load historical scan');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (preset) => {
    const now = new Date();
    let from, to = now;
    
    switch (preset) {
      case 'week':
        from = subWeeks(now, 1);
        break;
      case '2weeks':
        from = subDays(now, 14);
        break;
      case 'month':
        from = subMonths(now, 1);
        break;
      case '3months':
        from = subMonths(now, 3);
        break;
      default:
        from = subDays(now, 14);
    }
    
    setDateRange({ from, to });
  };

  const handleRefresh = () => {
    loadCustomScan(true);
  };

  const getImportanceColor = (score) => {
    if (score >= 75) return 'bg-red-500/10 text-red-600 border-red-500/20';
    if (score >= 50) return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'medium':
        return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      case 'low':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      default:
        return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  const getHighlightTypeColor = (type) => {
    switch (type) {
      case 'new':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'updated':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'stalled':
        return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      case 'risk':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'error':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  const getSourceTypeIcon = (sourceType) => {
    switch (sourceType) {
      case 'slack':
        return MessageSquare;
      case 'github':
        return GitPullRequest;
      case 'linear':
        return AlertCircle;
      case 'google-docs':
      case 'google_docs':
        return FileText;
      default:
        return FolderKanban;
    }
  };

  const getSourceTypeColor = (sourceType) => {
    switch (sourceType) {
      case 'slack':
        return 'text-purple-500';
      case 'github':
        return 'text-orange-500';
      case 'linear':
        return 'text-blue-500';
      case 'google-docs':
      case 'google_docs':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  if (loadingProjects || (loading && !data)) {
    return (
      <AppShell>
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-96" />
            </div>
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="flex items-center justify-center py-8">
            <ThinkingIndicator category="scanning" size="lg" />
          </div>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </AppShell>
    );
  }

  const {
    project_id,
    project_name,
    sources = [],
    timestamp,
    unanswered_questions = [],
    since,
    until
  } = data || {};
  
  const totalChanges = sources.reduce((sum, source) => sum + (source.total_changes || 0), 0);
  const totalHighlights = sources.reduce((sum, source) => sum + (source.highlights?.length || 0), 0);

  const selectedProject = projects.find(p => p.id === selectedProjectId);
  const daysDiff = dateRange.from && dateRange.to ? differenceInDays(dateRange.to, dateRange.from) : 0;
  const maxDaysExceeded = daysDiff > 90;

  return (
    <AppShell>
      <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="custom-scan-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">Custom Scan</h1>
            <p className="text-muted-foreground">
              View updates from connected sources for a custom time range
            </p>
            {timestamp && !refreshing && (
              <p className="text-sm text-muted-foreground mt-1">
                Last updated: {formatDate(timestamp)}
              </p>
            )}
            {refreshing && (
              <ThinkingIndicator category="scanning" className="mt-2" />
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Project Selector */}
            <Select value={selectedProjectId || ''} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select project">
                  {selectedProject ? selectedProject.name : 'Select project'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-2">
                      <FolderKanban className="h-4 w-4" />
                      {project.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              disabled={refreshing || !selectedProjectId}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Date Range Selector */}
        {selectedProjectId && (
          <Card>
            <CardHeader>
              <CardTitle>Select Time Range</CardTitle>
              <CardDescription>
                Choose a date range to scan (maximum 3 months)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Presets */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset('week')}
                >
                  Last Week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset('2weeks')}
                >
                  Last 2 Weeks
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset('month')}
                >
                  Last Month
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset('3months')}
                >
                  Last 3 Months
                </Button>
              </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Start Date</label>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !dateRange.from && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.from ? format(dateRange.from, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => {
                          setDateRange(prev => ({ ...prev, from: date }));
                          setStartDateOpen(false);
                        }}
                        disabled={(date) => {
                          if (dateRange.to) {
                            return date > dateRange.to || date > new Date();
                          }
                          return date > new Date();
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">End Date</label>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !dateRange.to && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.to ? format(dateRange.to, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateRange.to}
                        onSelect={(date) => {
                          setDateRange(prev => ({ ...prev, to: date }));
                          setEndDateOpen(false);
                        }}
                        disabled={(date) => {
                          if (dateRange.from) {
                            return date < dateRange.from || date > new Date();
                          }
                          return date > new Date();
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="pt-6">
                  <Button
                    onClick={() => loadCustomScan()}
                    disabled={loading || !dateRange.from || !dateRange.to || maxDaysExceeded}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Apply Scan
                  </Button>
                </div>
              </div>

              {/* Validation Messages */}
              {dateRange.from && dateRange.to && (
                <div className="text-sm">
                  {maxDaysExceeded ? (
                    <p className="text-red-600">
                      Date range exceeds 3 months ({daysDiff} days). Please select a shorter range.
                    </p>
                  ) : loading ? (
                    <ThinkingIndicator category="scanning" />
                  ) : (
                    <p className="text-muted-foreground">
                      Selected range: {daysDiff} day{daysDiff !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Historical Scans */}
              {historicalScans.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <History className="h-4 w-4" />
                    <h3 className="text-sm font-semibold">Previous Scans</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {historicalScans.slice(0, 10).map((scan) => (
                      <Button
                        key={scan.id}
                        variant={selectedScanId === scan.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => loadHistoricalScan(scan.id)}
                        className="text-xs"
                      >
                        {scan.since && scan.until ? (
                          <>
                            {format(new Date(scan.since), "MMM d")} - {format(new Date(scan.until), "MMM d")}
                          </>
                        ) : (
                          format(new Date(scan.created_at), "MMM d, yyyy")
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Project Info */}
        {selectedProject && (
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <FolderKanban className={cn("h-5 w-5", selectedProject.color && `text-[${selectedProject.color}]`)} />
                <div>
                  <h3 className="font-semibold">{project_name || selectedProject.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {sources.length} source{sources.length !== 1 ? 's' : ''} connected
                    {since && until && (
                      <> • {format(new Date(since), "MMM d")} - {format(new Date(until), "MMM d, yyyy")}</>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        {totalChanges > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Changes</p>
                    <p className="text-2xl font-bold">{totalChanges}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Highlights</p>
                    <p className="text-2xl font-bold">{totalHighlights}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sources</p>
                    <p className="text-2xl font-bold">{sources.length}</p>
                  </div>
                  <FolderKanban className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {(!selectedProjectId || totalChanges === 0) && !loading && (
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {!selectedProjectId ? 'Select a project to view custom scan' : 'No updates to show'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {!selectedProjectId 
                  ? 'Choose a project from the dropdown above to see updates from connected sources.'
                  : 'Your custom scan is empty. This could mean:'}
              </p>
              {selectedProjectId && (
                <ul className="text-sm text-muted-foreground space-y-1 mb-6">
                  <li>• No sources are connected to this project</li>
                  <li>• No activity in the selected time range</li>
                  <li>• Sources may not have synced yet</li>
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Unanswered Questions */}
        {unanswered_questions && unanswered_questions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-orange-500" />
                Unanswered Questions
              </CardTitle>
              <CardDescription>
                Questions in Slack that haven't been answered
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {unanswered_questions.map((q, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          {q.channel_name}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {q.hours_unanswered?.toFixed(1) || 'N/A'}h unanswered
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <span className="font-medium">{q.author}:</span> {q.question_text}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(q.created_at)}
                        </span>
                        {q.watchpoint_name && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {q.watchpoint_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sources - Same rendering as MorningScanPage */}
        {sources.map((source, sourceIdx) => {
          const SourceIcon = getSourceTypeIcon(source.source_type);
          const sourceColor = getSourceTypeColor(source.source_type);
          
          return (
            <div key={source.source_id || sourceIdx} className="space-y-4">
              {/* Source Header */}
              <div className="flex items-center gap-3">
                <SourceIcon className={cn("h-6 w-6", sourceColor)} />
                <div className="flex-1">
                  <h2 className="text-xl font-semibold">{source.source_label}</h2>
                  <p className="text-sm text-muted-foreground">
                    {source.total_changes} change{source.total_changes !== 1 ? 's' : ''}
                    {since && until && (
                      <> from {format(new Date(since), "MMM d")} to {format(new Date(until), "MMM d")}</>
                    )}
                  </p>
                </div>
                <Badge variant="secondary">{source.source_type}</Badge>
              </div>

              {/* Highlights */}
              {source.highlights && source.highlights.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    Highlights
                  </h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    {source.highlights.map((highlight, idx) => {
                      const HighlightIcon = highlight.type === 'new' ? CheckCircle2 
                        : highlight.type === 'risk' || highlight.type === 'stalled' ? AlertTriangle
                        : highlight.type === 'error' ? AlertCircle
                        : Info;
                      
                      return (
                        <Card key={idx} className={cn("hover:bg-muted/50 transition-colors", getHighlightTypeColor(highlight.type))}>
                          <CardContent className="pt-4">
                            <div className="flex items-start gap-3">
                              <HighlightIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold mb-1">{highlight.title}</h4>
                                {highlight.description && (
                                  <p className="text-sm text-muted-foreground mb-2">{highlight.description}</p>
                                )}
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  {formatDate(highlight.timestamp)}
                                </div>
                              </div>
                              {highlight.url && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="flex-shrink-0"
                                  onClick={() => window.open(highlight.url, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Items */}
              {source.items && source.items.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    Recent Activity
                  </h3>
                  <div className="space-y-2">
                    {source.items.map((item, idx) => {
                      const ItemIcon = item.type === 'pr' ? GitPullRequest
                        : item.type === 'issue' ? AlertCircle
                        : item.type === 'message' ? MessageSquare
                        : item.type === 'doc' ? FileText
                        : FolderKanban;

                      return (
                        <Card key={idx} className="hover:bg-muted/50 transition-colors">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <ItemIcon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", sourceColor)} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-semibold truncate">{item.title}</h4>
                                    {item.status && (
                                      <Badge variant="outline" className="text-xs flex-shrink-0">
                                        {item.status}
                                      </Badge>
                                    )}
                                  </div>
                                  {item.metadata && Object.keys(item.metadata).length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-2">
                                      {item.metadata.author && (
                                        <span className="text-xs text-muted-foreground">
                                          by {item.metadata.author}
                                        </span>
                                      )}
                                      {item.metadata.priority !== undefined && item.metadata.priority !== null && (
                                        <Badge variant="outline" className="text-xs">
                                          Priority: {item.metadata.priority === 1 ? 'Urgent' : item.metadata.priority === 2 ? 'High' : item.metadata.priority === 3 ? 'Normal' : 'Low'}
                                        </Badge>
                                      )}
                                      {item.metadata.merged !== undefined && (
                                        <Badge variant={item.metadata.merged ? "default" : "outline"} className="text-xs">
                                          {item.metadata.merged ? 'Merged' : 'Open'}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {formatDate(item.timestamp)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {item.url && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="flex-shrink-0"
                                  onClick={() => window.open(item.url, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}

